import { QuillCommands } from "components/Quill";
import ImageUrlBlot from "components/Quill/Blots/ImageUrlBlot";
import VideoUrlBlot from "components/Quill/Blots/VideoUrlBlot";
import ClipboardExt from "components/Quill/ClipboardExt";
import ImageDropAndPaste from "components/Quill/ImageDropAndPaste";
import ImageResize from "components/Quill/ImageResize";
import MagicUrl from "components/Quill/MagicUrl";
import { MprcQuillEditor } from "components/Quill/MprcQuillEditor";
import { QuillProps } from "components/Quill/QuillProps";
import { MailModelPropertyDefinition } from "generated/CoreApi_v1";
import * as React from "react";
import { useImperativeHandle } from "react";

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    templateProperties?: MailModelPropertyDefinition[] | null;
}

type Props = IProps & QuillProps;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const TemplateEditorComponent: React.ForwardRefRenderFunction<QuillCommands, Props> = (props, forwardRef) => {
    const quillRef = React.useRef<QuillCommands>(null!);

    useImperativeHandle(forwardRef, () => ({
        focus: quillRef.current.focus,
        disable: quillRef.current.disable,
        insert: quillRef.current.insert,
        clear: quillRef.current.clear,
        setValue: quillRef.current.setValue,
        getQuillRef: quillRef.current.getQuillRef,
        getValueHtml: quillRef.current.getValueHtml,
        getValueText: quillRef.current.getValueText

    }));

    function initCustomField() {
        document.querySelectorAll('.ql-customField .ql-picker-label').forEach(target => {
            target.setAttribute('data-content', 'Campos');
        });

        const placeholderPickerItems = document.querySelectorAll('.ql-customField .ql-picker-item');
        placeholderPickerItems.forEach(item => item.textContent = props.templateProperties?.find(p => p.propName == (item as any).dataset.value)?.displayName ?? "?");
    }

    const customFieldHandler = (value: any) => {
        var q = quillRef.current.getQuillRef();
        if (value) {
            const cursorPosition = q.getSelection(undefined);
            if (cursorPosition != null) {
                q.insertText(cursorPosition.index, value);
                q.setSelection(cursorPosition.index + value.length, 0);
            }
        }
    };

    return <MprcQuillEditor
        {...props}
        ref={quillRef}
        modules={
            {
                toolbar: {
                    container: [
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],

                        ['bold', 'italic', 'underline', 'strike'],
                        [{ align: [] }],

                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ indent: '-1' }, { indent: '+1' }],

                        ['link', 'image'],
                        [{ color: [] }, { background: [] }],

                        [{ 'customField': props.templateProperties?.map(p => p.propName) }],

                        ['clean'],
                    ],
                    handlers: {
                        customField: customFieldHandler
                    }
                },
                imageDropAndPaste: {},
                imageResize: {},
                magicUrl: true,
                clipboardExt: {
                    image: true,
                    video: false,
                    youtube: false,
                },
                clipboard: {
                    // toggle to add extra line breaks when pasting HTML:
                    matchVisual: true,
                },
            }
        }
        formats={
            [
                'header', 'font', 'size', 'align', 'color', 'background',
                'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
                'list', 'bullet', 'indent', 'sub', 'super', 'direction', 'image', 'link'
            ]
        }
        onRegisterModules={(Quill: any) => {
            // For execute this line only once.
            Quill.register('modules/magicUrl', MagicUrl);
            Quill.register('modules/clipboardExt', ClipboardExt);
            Quill.register('modules/imageResize', ImageResize);
            Quill.register('modules/imageDropAndPaste', ImageDropAndPaste);
            // Blots
            Quill.register(ImageUrlBlot);
            Quill.register(VideoUrlBlot);
        }}
        onQuillReady={() => {
            initCustomField();
        }}
    >
    </MprcQuillEditor>;
};

export const TemplateEditor = React.forwardRef(TemplateEditorComponent);
